import { useState } from 'react';
import { grantWarningsFromError, useToast } from '@dataroom/shared';
import type { AccessGrant, CreateAccessGrantRequest, GrantWarning } from '@dataroom/shared';
import {
  useAccessGrants,
  useAddSeller,
  useCreateAccessGrant,
  useInviteParty,
  useRevokeGrant,
  useRoomActors,
  useRoomCategories,
  useRoomSellers,
  useSetTeamRoles,
} from '@/api/hooks';
import { AccessRightsCard } from '@/components/AccessRightsCard';
import { AddGrantDialog } from '@/components/AddGrantDialog';
import { TeamRolesDialog } from '@/components/TeamRolesDialog';
import { InvitePartyDialog } from '@/components/InvitePartyDialog';
import { RoomSellerCard } from '@/components/RoomSellerCard';
import { apiMessage, plainError } from '@/lib/errors';
import { useRoomContext } from './context';

/** PRD-0004 §3b — access rights: the room's sellers and what they own, who can see what, invite a party (role-based
 * default grant), add an advanced grant (advise + acknowledge), revoke. */
export function RoomAccessPage() {
  const { roomId: id, room } = useRoomContext();
  const grants = useAccessGrants(id);
  const actors = useRoomActors(id);
  const categories = useRoomCategories(id);

  const invite = useInviteParty(id);
  // "Seller" in the invite dialog adds a seller with their ownership share — the
  // same call as the Seller card's "Add a seller" (POST /rooms/:id/sellers).
  const addSeller = useAddSeller(id);
  const sellers = useRoomSellers(id);
  const sellerCount = sellers.data ? sellers.data.sellers.length : null;
  const setRoles = useSetTeamRoles(id);
  const [rolesFor, setRolesFor] = useState<AccessGrant | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  // Inviting someone already invited changes nothing on screen (one person, no
  // new grant), so say what happened (full-deal test 2026-10-08).
  const toast = useToast();

  const createGrant = useCreateAccessGrant(id);
  const [addGrantOpen, setAddGrantOpen] = useState(false);
  const [lastGrantBody, setLastGrantBody] = useState<CreateAccessGrantRequest | null>(null);
  const [ackWarnings, setAckWarnings] = useState<GrantWarning[]>([]);
  const grantWarnings = grantWarningsFromError(createGrant.error);

  const revoke = useRevokeGrant(id);

  const closeInvite = () => {
    setInviteOpen(false);
    invite.reset();
    addSeller.reset();
  };
  const closeAddGrant = () => {
    setAddGrantOpen(false);
    createGrant.reset();
    setLastGrantBody(null);
  };

  return (
    <div className="space-y-6">
      <RoomSellerCard room={room} />

      <AccessRightsCard
        grants={grants.data ?? []}
        loading={grants.isPending}
        error={apiMessage(grants.error)}
        onInvite={() => {
          invite.reset();
          addSeller.reset();
          setInviteOpen(true);
        }}
        onAddGrant={() => {
          createGrant.reset();
          setLastGrantBody(null);
          setAckWarnings([]);
          setAddGrantOpen(true);
        }}
        onRevoke={(grantId) => revoke.mutate(grantId)}
        sellerActorIds={(sellers.data?.sellers ?? []).map((x) => x.actorId)}
        onEditRoles={(grant) => {
          setRoles.reset();
          setRolesFor(grant);
        }}
        revokingId={revoke.isPending ? (revoke.variables ?? null) : null}
        ackWarnings={ackWarnings}
        adminRole={room.adminRole}
        onDismissAckWarnings={() => setAckWarnings([])}
      />

      {inviteOpen ? (
        <InvitePartyDialog
          adminRole={room.adminRole}
          sellerCount={sellerCount}
          pending={invite.isPending || addSeller.isPending}
          error={apiMessage(invite.error ?? addSeller.error)}
          onClose={closeInvite}
          onAddSeller={(body) =>
            addSeller.mutate(body, {
              onSuccess: (res) => {
                closeInvite();
                toast({
                  message: res.canApprove
                    ? `${body.email} is now one of this room’s owners — they sign in to the seller portal with this email address`
                    : `${body.email} is now one of this room’s owners. ${res.warning ?? ''} Set the percentages in the Owners card above`,
                });
              },
            })
          }
          onSubmit={(body) =>
            invite.mutate(body, {
              onSuccess: () => {
                closeInvite();
                toast({
                  message: `Invited ${body.email ?? 'them'} — they sign in with a code we email them`,
                });
              },
            })
          }
        />
      ) : null}

      {rolesFor && rolesFor.actorId ? (
        <TeamRolesDialog
          who={rolesFor.actorEmail ?? 'this person'}
          initial={rolesFor.teamRoles ?? []}
          pending={setRoles.isPending}
          error={apiMessage(setRoles.error)}
          onClose={() => setRolesFor(null)}
          onSave={(roles) =>
            setRoles.mutate(
              { actorId: rolesFor.actorId as string, roles },
              { onSuccess: () => setRolesFor(null) },
            )
          }
        />
      ) : null}

      {addGrantOpen ? (
        <AddGrantDialog
          adminRole={room.adminRole}
          actors={actors.data ?? []}
          categories={categories.data ?? []}
          pending={createGrant.isPending}
          error={plainError(createGrant.error)}
          warnings={grantWarnings}
          onClose={closeAddGrant}
          onSubmit={(body) => {
            setLastGrantBody(body);
            createGrant.mutate(body, {
              onSuccess: (res) => {
                setAckWarnings(res.warnings ?? []);
                closeAddGrant();
              },
            });
          }}
          onProceed={() => {
            if (!lastGrantBody) return;
            createGrant.mutate(
              { ...lastGrantBody, acknowledgeWarnings: true },
              {
                onSuccess: (res) => {
                  setAckWarnings(res.warnings ?? []);
                  closeAddGrant();
                },
              },
            );
          }}
          onCancelWarnings={closeAddGrant}
        />
      ) : null}
    </div>
  );
}
